import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, RefreshCw, Loader2, Users, Star, Trash2, Search } from 'lucide-react';
import api, { LOCATIONS } from '@/lib/api';
import { toast } from 'sonner';

const DISCIPLINES = [
  'Structural Engineer', 'Quantity Surveyor', 'Builder / Contractor', 'Interior Designer',
  'Landscape Architect', 'Sustainability Consultant', 'Electrical Engineer', 'Mechanical Engineer',
  'Project Manager', 'Heritage Consultant', 'Town Planner', 'Land Surveyor', 'Other',
];

const AVAILABILITY = { available: 'bg-green-50 text-green-700', busy: 'bg-amber-50 text-amber-700', unavailable: 'bg-red-50 text-red-700' };

export default function ProfessionalNetwork() {
  const [professionals, setProfessionals] = useState([]);
  const [filter, setFilter] = useState('');
  const [disciplineFilter, setDisciplineFilter] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: '', discipline: '', firm: '', email: '', phone: '',
    regions: [], skills: '', internal_rating: 0, internal_notes: '', availability: 'available',
  });

  useEffect(() => { fetch(); }, []);

  const fetch = async () => {
    try { const { data } = await api.get('/studio/professionals'); setProfessionals(data); } catch {}
  };

  const addProfessional = async () => {
    if (!form.name || !form.discipline) { toast.error('Name and discipline required'); return; }
    setLoading(true);
    try {
      await api.post('/studio/professionals', {
        ...form,
        skills: form.skills ? form.skills.split(',').map(s => s.trim()).filter(Boolean) : [],
      });
      setForm({ name: '', discipline: '', firm: '', email: '', phone: '', regions: [], skills: '', internal_rating: 0, internal_notes: '', availability: 'available' });
      setShowForm(false);
      fetch();
      toast.success('Professional added');
    } catch { toast.error('Failed to add'); }
    finally { setLoading(false); }
  };

  const deleteProfessional = async (id) => {
    if (!window.confirm('Remove this professional?')) return;
    try { await api.delete(`/studio/professionals/${id}`); fetch(); toast.success('Removed'); } catch {}
  };

  const updateAvailability = async (id, availability) => {
    try { await api.patch(`/studio/professionals/${id}`, { availability }); fetch(); } catch {}
  };

  const toggleRegion = (region) => {
    setForm(p => ({
      ...p, regions: p.regions.includes(region) ? p.regions.filter(r => r !== region) : [...p.regions, region],
    }));
  };

  const filtered = professionals.filter(p => {
    if (filter && !p.name.toLowerCase().includes(filter.toLowerCase()) && !p.firm?.toLowerCase().includes(filter.toLowerCase())) return false;
    if (disciplineFilter !== 'all' && p.discipline !== disciplineFilter) return false;
    return true;
  });

  return (
    <div data-testid="network-section">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-brand-dark/50 font-body">{professionals.length} professionals in network</p>
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" onClick={fetch}><RefreshCw className="w-4 h-4" /></Button>
          <Button size="sm" onClick={() => setShowForm(!showForm)} data-testid="add-professional-btn"
            className="bg-brand-dark text-white hover:bg-gray-800 rounded-sm font-body text-xs uppercase tracking-wider">
            <Plus className="w-3.5 h-3.5 mr-1" /> Add Professional
          </Button>
        </div>
      </div>

      {/* Add Form */}
      {showForm && (
        <div className="bg-white rounded-sm border border-black/10 p-6 mb-4" data-testid="add-professional-form">
          <h3 className="font-heading text-sm font-bold uppercase tracking-wider text-brand-dark/60 mb-4">New Professional</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
            <Input value={form.name} onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Name *" className="rounded-sm h-9 text-xs" data-testid="prof-name" />
            <Select value={form.discipline} onValueChange={(v) => setForm(p => ({ ...p, discipline: v }))}>
              <SelectTrigger className="rounded-sm h-9 text-xs" data-testid="prof-discipline"><SelectValue placeholder="Discipline *" /></SelectTrigger>
              <SelectContent>{DISCIPLINES.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
            </Select>
            <Input value={form.firm} onChange={(e) => setForm(p => ({ ...p, firm: e.target.value }))} placeholder="Firm name" className="rounded-sm h-9 text-xs" data-testid="prof-firm" />
            <Input value={form.email} onChange={(e) => setForm(p => ({ ...p, email: e.target.value }))} placeholder="Email" className="rounded-sm h-9 text-xs" />
            <Input value={form.phone} onChange={(e) => setForm(p => ({ ...p, phone: e.target.value }))} placeholder="Phone" className="rounded-sm h-9 text-xs" />
            <Input value={form.skills} onChange={(e) => setForm(p => ({ ...p, skills: e.target.value }))} placeholder="Skills (comma-separated)" className="rounded-sm h-9 text-xs" />
          </div>
          <div className="mb-3">
            <p className="text-xs uppercase tracking-wider font-semibold text-brand-dark/60 mb-2">Regions Served</p>
            <div className="flex flex-wrap gap-2">
              {LOCATIONS.map(l => (
                <button key={l.value} onClick={() => toggleRegion(l.value)} type="button"
                  className={`px-2.5 py-1 rounded-sm text-xs font-body border transition-colors ${form.regions.includes(l.value) ? 'bg-brand-accent/20 border-brand-accent/40 text-brand-dark' : 'bg-white border-black/10 text-brand-dark/50 hover:border-brand-accent/30'}`}>
                  {l.label}
                </button>
              ))}
            </div>
          </div>
          <Textarea value={form.internal_notes} onChange={(e) => setForm(p => ({ ...p, internal_notes: e.target.value }))} placeholder="Internal notes (private)" className="rounded-sm min-h-[60px] mb-3 text-xs" />
          <div className="flex gap-2">
            <Button onClick={addProfessional} disabled={loading} className="bg-brand-dark text-white rounded-sm text-xs" data-testid="prof-submit">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add Professional'}
            </Button>
            <Button variant="ghost" onClick={() => setShowForm(false)}>Cancel</Button>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-3 mb-4">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-brand-dark/30" />
          <Input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Search..." className="rounded-sm h-9 text-xs pl-8" data-testid="network-search" />
        </div>
        <Select value={disciplineFilter} onValueChange={setDisciplineFilter}>
          <SelectTrigger className="w-48 rounded-sm h-9 text-xs" data-testid="network-discipline-filter"><SelectValue placeholder="All Disciplines" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Disciplines</SelectItem>
            {DISCIPLINES.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* List */}
      {filtered.length === 0 ? (
        <div className="bg-white rounded-sm border border-black/10 p-12 text-center">
          <Users className="w-10 h-10 text-brand-dark/15 mx-auto mb-4" />
          <h3 className="font-heading text-lg font-medium text-brand-dark mb-2">{professionals.length === 0 ? 'Build your network' : 'No results'}</h3>
          <p className="text-brand-dark/40 font-body text-sm max-w-md mx-auto">
            {professionals.length === 0
              ? 'Add the engineers, QS professionals, builders, and consultants your practice works with. This becomes institutional memory.'
              : 'Try adjusting your search or filter.'}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((prof) => (
            <div key={prof.professional_id} className="bg-white rounded-sm border border-black/10 p-4" data-testid={`prof-card-${prof.professional_id}`}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-body font-medium">{prof.name}</p>
                    <Badge variant="outline" className="text-xs capitalize">{prof.discipline}</Badge>
                    <Badge variant="outline" className={`text-xs capitalize ${AVAILABILITY[prof.availability] || AVAILABILITY.available}`}>{prof.availability}</Badge>
                    {prof.internal_rating >= 4 && <Star className="w-3.5 h-3.5 text-brand-accent fill-brand-accent" />}
                  </div>
                  <p className="text-xs text-brand-dark/40 font-body mt-0.5">
                    {prof.firm && `${prof.firm} · `}
                    {prof.regions?.map(r => LOCATIONS.find(l => l.value === r)?.label || r).join(', ')}
                    {prof.skills?.length > 0 && ` · ${prof.skills.join(', ')}`}
                  </p>
                  {prof.internal_notes && <p className="text-xs text-brand-dark/30 font-body mt-1 italic">{prof.internal_notes}</p>}
                </div>
                <div className="flex items-center gap-1.5">
                  <Select value={prof.availability} onValueChange={(v) => updateAvailability(prof.professional_id, v)}>
                    <SelectTrigger className="w-28 h-8 rounded-sm text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="available">Available</SelectItem>
                      <SelectItem value="busy">Busy</SelectItem>
                      <SelectItem value="unavailable">Unavailable</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button variant="ghost" size="sm" onClick={() => deleteProfessional(prof.professional_id)} className="h-8 w-8 p-0">
                    <Trash2 className="w-3.5 h-3.5 text-red-400" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
